import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ADMIN_BASE } from '../utils/apiBase';

const AdminContext = createContext();

const API = ADMIN_BASE;

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      try {
        const response = await axios.get(`${API}/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAdmin(response.data);
      } catch (error) {
        localStorage.removeItem('admin_token');
        setAdmin(null);
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/login`, { email, password });
      localStorage.setItem('admin_token', response.data.access_token);
      setAdmin({ email: response.data.email });
      toast.success('Login successful!');
      navigate('/admin/dashboard');
      return true;
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (!error.response) {
        toast.error('Backend is unreachable. Check backend server and URL.');
      } else if (status === 404) {
        toast.error('Admin login API route not found. Check API base URL and backend routes.');
      } else {
        toast.error(detail || 'Invalid credentials');
      }
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setAdmin(null);
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const value = {
    admin,
    loading,
    login,
    logout,
    getAuthHeader,
    isAuthenticated: !!admin
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};
